import { Box, Breadcrumbs, Container, Link as MuiLink, Typography } from '@mui/material';
import { JsonLd } from '@presentation/seo/JsonLd';
import { buildBreadcrumb } from '@presentation/seo/schema';

export type BenefitTopicSummary = {
	slug: string;
	title: string;
	intro: string;
};

type BenefitsHubProps = {
	topics: BenefitTopicSummary[];
};

const BenefitsHub = ({ topics }: BenefitsHubProps) => (
	<Container maxWidth='md' sx={{ py: { xs: 3, md: 5 } }}>
		<JsonLd data={buildBreadcrumb([{ name: 'Home', url: '/' }, { name: 'Benefits' }])} />

		<Breadcrumbs sx={{ mb: 2, fontSize: 14 }}>
			<MuiLink
				href='/'
				sx={{
					color: (theme) => theme.colors.brand,
					textDecoration: 'none',
					'&:hover': { textDecoration: 'underline' },
				}}
			>
				Home
			</MuiLink>
			<Typography component='span' sx={{ fontSize: 14, color: 'text.secondary' }}>
				Benefits
			</Typography>
		</Breadcrumbs>

		<Typography component='h1' sx={{ fontSize: { xs: 26, md: 34 }, fontWeight: 800, mb: 2, textWrap: 'balance' }}>
			The benefits of jigsaw puzzles
		</Typography>
		<Typography
			component='p'
			sx={{ fontSize: { xs: 17, md: 19 }, lineHeight: 1.6, color: 'text.primary', mb: 4, textWrap: 'pretty' }}
		>
			What does the research actually say about doing jigsaw puzzles? Explore evidence-based summaries on how puzzles
			relate to your brain, memory, stress, and healthy aging — each compiled from peer-reviewed studies.
		</Typography>

		<Box sx={{ display: 'grid', gap: 2 }}>
			{topics.map((topic) => (
				<MuiLink
					key={topic.slug}
					href={`/benefits/${topic.slug}`}
					sx={{
						textDecoration: 'none',
						display: 'block',
						p: 3,
						borderRadius: '16px',
						border: '1px solid rgba(0, 0, 0, 0.08)',
						transition: 'box-shadow 0.15s ease, transform 0.15s ease',
						'&:hover': { boxShadow: '0 6px 20px rgba(0, 0, 0, 0.08)', transform: 'translateY(-2px)' },
					}}
				>
					<Typography component='h2' sx={{ fontSize: 20, fontWeight: 700, mb: 0.5, color: 'text.primary' }}>
						{topic.title}
					</Typography>
					<Typography component='p' sx={{ fontSize: 15, lineHeight: 1.6, color: 'text.secondary' }}>
						{topic.intro}
					</Typography>
				</MuiLink>
			))}
		</Box>
	</Container>
);

export default BenefitsHub;
